<template>
  <div
    id="aiWritingBrief"
    class="writing-brief"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <div class="writing-brief__strip">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="accent"
        height="3"
        class="writing-brief__progress"
      />
      <span
        v-if="loading"
        class="writing-brief__sr-only"
      >{{ $t('aiBrief.loadingAria') }}</span>
      <div class="writing-brief__head">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <button
              type="button"
              class="writing-brief__avatar"
              v-bind="attrs"
              :aria-label="$t('aiBrief.avatarAria')"
              v-on="on"
              @click="openLucy"
            >
              L
            </button>
          </template>
          <span>{{ $t('aiBrief.avatarTooltip') }}</span>
        </v-tooltip>
        <div class="writing-brief__head-copy">
          <div class="writing-brief__title-row">
            <strong class="writing-brief__title">{{ $t('aiBrief.title') }}</strong>
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="16"
              width="2"
              color="accent"
              class="writing-brief__head-spinner"
            />
            <BriefActionTooltip
              v-if="brief.intent"
              :text="$t('aiBrief.intentTip')"
            >
              <v-chip
                small
                outlined
                color="primary"
                class="ma-1 text-none writing-brief__chip"
              >
                {{ $t('aiBrief.intent.' + brief.intent.label) }}
              </v-chip>
            </BriefActionTooltip>
            <v-skeleton-loader
              v-else-if="loading"
              type="chip"
              class="ma-1 writing-brief__chip-skel"
            />
            <BriefActionTooltip
              v-if="brief.snippetShape"
              :text="$t('aiBrief.shapeChipTip')"
            >
              <v-chip
                small
                outlined
                class="ma-1 text-none writing-brief__chip"
                :color="brief.keepAnswerShape ? 'accent' : undefined"
              >
                {{ $t('aiBrief.shape.' + brief.snippetShape) }}
              </v-chip>
            </BriefActionTooltip>
            <v-skeleton-loader
              v-else-if="loading"
              type="chip"
              class="ma-1 writing-brief__chip-skel"
            />
            <BriefActionTooltip
              v-if="brief.snippetShape && !actionsDisabled"
              :text="$t(brief.keepAnswerShape ? 'aiBrief.keepShapeOnTip' : 'aiBrief.keepShapeTip')"
            >
              <v-btn
                x-small
                depressed
                class="ma-1 text-none"
                :color="brief.keepAnswerShape ? 'accent' : undefined"
                @click="onKeepShape"
              >
                {{ $t(brief.keepAnswerShape ? 'aiBrief.keepShapeOn' : 'aiBrief.keepShape') }}
              </v-btn>
            </BriefActionTooltip>
            <v-skeleton-loader
              v-else-if="loading && !actionsDisabled"
              type="button"
              class="ma-1 writing-brief__chip-skel"
            />
          </div>
          <div
            v-if="headerLoading"
            class="writing-brief__wait"
          >
            {{ $t('aiBrief.loadingHeader') }}
          </div>
          <div class="writing-brief__subtitle">
            {{ $t('aiBrief.subtitle') }}
          </div>
        </div>
        <v-tooltip
          v-if="showWriteCta"
          top
          :disabled="!writeCtaTooltip"
        >
          <template #activator="{ on }">
            <span
              class="writing-brief__enhance-wrap"
              v-on="on"
            >
              <v-btn
                id="briefGenerateButton"
                depressed
                color="accent"
                class="text-none writing-brief__enhance"
                :loading="writeBusy"
                :disabled="writeCtaDisabled"
                @click="onWriteCta"
              >
                <v-icon
                  left
                  small
                >
                  send
                </v-icon>
                {{ writeCtaLabel }}
              </v-btn>
            </span>
          </template>
          {{ writeCtaTooltip }}
        </v-tooltip>
        <div
          v-if="showWriteCta && enhanceWaitingForPage"
          class="caption text--secondary mt-1"
          data-test="enhance-loading-page"
        >
          {{ $t('article.manualEnhance.loadingPage') }}
        </div>
      </div>

      <div
        v-if="canApplyTemplate && !actionsDisabled"
        class="writing-brief__template-hint"
      >
        <span>{{ $t('aiBrief.templateHint', [$t('createBrief.templates.' + brief.suggestedGeoTemplate + '.title')]) }}</span>
        <BriefActionTooltip :text="$t('aiBrief.useTemplateTip')">
          <v-btn
            x-small
            depressed
            color="accent"
            class="text-none ml-2"
            @click="$emit('apply-template', brief.suggestedGeoTemplate)"
          >
            {{ $t('aiBrief.useTemplate') }}
          </v-btn>
        </BriefActionTooltip>
      </div>

      <KeywordWorkspaceGroup
        group-id="g-live"
        :title="$t('aiBrief.layerLive')"
        :subtitle="$t('aiBrief.answersHint')"
        :count-label="$t('keywords.list.workspace.liveCount', [liveCards.length])"
        number="1"
        live
        :open="liveOpen"
        @toggle="liveOpen = !liveOpen"
      >
        <div class="writing-brief__live-grid">
          <div
            v-for="card in liveCards"
            :key="card.id"
          >
            <div
              v-if="liveCardLoading(card.id)"
              class="writing-brief__hint writing-brief__hint--skel"
            >
              <div class="writing-brief__hint-title">
                {{ $t('aiBrief.platforms.' + card.id) }}
              </div>
              <v-skeleton-loader
                type="sentences"
                aria-hidden="true"
              />
            </div>
            <div
              v-else
              class="writing-brief__hint"
            >
              <div class="writing-brief__hint-title">
                {{ $t('aiBrief.platforms.' + card.id) }}
              </div>
              <p
                v-if="!card.text"
                class="writing-brief__empty"
              >
                {{ $t('aiBrief.emptyLiveAnswer') }}
              </p>
              <div
                v-else
                class="writing-brief__answer-wrap"
              >
                <p
                  ref="liveAnswerBodies"
                  class="writing-brief__answer"
                  :class="{ 'writing-brief__answer--collapsed': !isLiveExpanded(card.id) }"
                  :data-platform="card.id"
                >{{ card.text }}</p>
                <v-btn
                  v-if="showLiveToggle(card)"
                  x-small
                  text
                  class="text-none writing-brief__answer-toggle"
                  :aria-expanded="isLiveExpanded(card.id) ? 'true' : 'false'"
                  @click="toggleLiveAnswer(card.id)"
                >
                  {{ isLiveExpanded(card.id) ? $t('aiBrief.collapseAnswer') : $t('aiBrief.expandAnswer') }}
                </v-btn>
              </div>
              <BriefActionTooltip
                v-if="card.text"
                :text="workInTip(card.id)"
              >
                <v-btn
                  x-small
                  class="text-none mt-1"
                  :outlined="!isWorkedIn(card.id)"
                  :depressed="isWorkedIn(card.id)"
                  :color="isWorkedIn(card.id) ? 'accent' : undefined"
                  :disabled="actionsDisabled || isWorkedIn(card.id)"
                  @click="onWorkInLive(card.id)"
                >
                  {{ isWorkedIn(card.id) ? $t('aiBrief.workedIn') : $t('aiBrief.workIn') }}
                </v-btn>
              </BriefActionTooltip>
            </div>
          </div>
        </div>
        <p
          v-if="liveSectionLoading"
          class="writing-brief__wait px-3 pb-2"
        >
          {{ $t('aiBrief.loadingMore') }}
        </p>
      </KeywordWorkspaceGroup>

      <KeywordWorkspaceGroup
        v-if="citedSources.length || (loading && !citedSources.length && hasLiveContent)"
        group-id="g-cite"
        :title="$t('aiBrief.citedSources')"
        :subtitle="$t('aiBrief.citedSourcesHint')"
        :count-label="$t('keywords.list.workspace.citeCount', [citedSources.length])"
        number="2"
        live
        :open="citeOpen"
        @toggle="citeOpen = !citeOpen"
      >
        <v-skeleton-loader
          v-if="loading && !citedSources.length"
          type="list-item-two-line@3"
          class="px-2"
        />
        <div
          v-for="source in citedSources"
          :key="source.url"
          class="kw-row kw-row--cite"
        >
          <div class="kw-row__mid">
            <div class="kw-row__phrase">{{ source.title }}</div>
            <div class="kw-row__meta">
              <span class="kw-src kw-src--chat">{{ $t('aiBrief.sources.mentions') }}</span>
              <span class="writing-brief__cite-hint">{{ source.domain || source.url }} · {{ platformLabel(source.platform) }}</span>
            </div>
          </div>
          <div class="kw-row__pills">
            <BriefActionTooltip :text="$t(isCitationSelected(source.url) ? 'aiBrief.citedSelectedTip' : 'aiBrief.citeForLucyTip')">
              <v-btn
                depressed
                data-test="cite-for-lucy"
                class="text-none kw-pill"
                :class="isCitationSelected(source.url) ? 'kw-pill--on' : 'kw-pill--lucy'"
                :aria-pressed="isCitationSelected(source.url) ? 'true' : 'false'"
                :disabled="actionsDisabled"
                @click="onToggleCitation(source.url)"
              >
                <v-icon
                  left
                  x-small
                >
                  {{ isCitationSelected(source.url) ? 'check' : 'add' }}
                </v-icon>
                {{ $t('keywords.list.header.forAiLucy') }}
              </v-btn>
            </BriefActionTooltip>
          </div>
        </div>
      </KeywordWorkspaceGroup>

      <div class="writing-brief__layer">
        <div class="writing-brief__layer-h">
          <span class="writing-brief__num writing-brief__num--kw">3–8</span>
          {{ $t('keywords.allKeywords') }}
          <span class="writing-brief__layer-hint">— {{ $t('keywords.list.workspace.analysisHint') }}</span>
        </div>
        <div
          id="analysisBar"
          class="writing-brief__kw-bar"
        >
          <div>
            <strong>{{ $t('keywords.allKeywords') }}</strong>
            <span class="writing-brief__kw-meta">
              {{ $t(keywordSelectionShown ? 'aiBrief.analysisMetaOpen' : 'aiBrief.analysisMeta', [keywordGroupCount, lucySlotCount, MAX_AI_KEYWORDS]) }}
            </span>
            <div class="writing-brief__jumps">
              <button
                v-for="jump in analysisJumps"
                :key="jump.id"
                type="button"
                class="writing-brief__jump"
                @click="jumpToGroup(jump.id)"
              >
                {{ jump.label }}
              </button>
            </div>
          </div>
          <BriefActionTooltip :text="$t(keywordSelectionShown ? 'aiBrief.collapseAnalysisTip' : 'aiBrief.expandAnalysisTip')">
            <v-btn
              outlined
              class="text-none writing-brief__kw-toggle"
              @click="toggleKeywordAnalysis"
            >
              <v-icon
                left
                small
              >
                {{ keywordSelectionShown ? 'unfold_less' : 'unfold_more' }}
              </v-icon>
              {{ keywordSelectionShown ? $t('aiBrief.collapseAnalysis') : $t('aiBrief.expandAnalysis') }}
            </v-btn>
          </BriefActionTooltip>
        </div>
      </div>
    </div>
    <p
      v-if="statusSentence"
      class="writing-brief__status"
      aria-live="polite"
    >
      {{ statusSentence }}
    </p>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import {Article, KeywordForAi} from '@/model/Article';
    import type {AiAnswerSnapshot, AiWritingBrief} from '@/model/AiWritingBrief';
    import {emptyAiWritingBrief} from '@/service/aiWritingBrief';
    import {liveAnswerBody, liveAnswerNeedsExpand} from '@/service/aiBriefCopy';
    import {openLucyPanel} from '@/service/lucyCoach';
    import type {GeoTemplateId} from '@/config/WritingBrief';
    import BriefActionTooltip from '@/components/keywords/BriefActionTooltip.vue';
    import KeywordWorkspaceGroup from '@/components/keywords/KeywordWorkspaceGroup.vue';
    import {KeywordType} from '@/enum/keyword_type';
    import {MAX_AI_KEYWORDS} from '@/utils/Article';
    import {countLucySlots} from '@/service/keywordLucySelection';
    import {EventBus} from '@/utils/EventBus';
    import {
        articleHasGeneratedBody,
        enhanceWaitingForExtraction,
        isManualReEnhanceArticle,
        shouldShowKeywordWorkspaceWriteCta,
    } from '@/utils/articleWriteCta';
    import type {LiveBriefPlatform} from '@/service/briefEditorActions';
    import {
        keepAnswerShape,
        liveCardPlatforms,
        syncSelectedBriefIntoKeywords,
    } from '@/service/briefEditorActions';

    type LiveCard = {id: LiveBriefPlatform; text: string; expandable: boolean};

    export default Vue.extend({
        name: 'AiWritingBriefPanel',
        components: {BriefActionTooltip, KeywordWorkspaceGroup},
        props: {
            brief: {
                type: Object as PropType<AiWritingBrief>,
                default: () => emptyAiWritingBrief()
            },
            loading: {
                type: Boolean,
                default: false
            },
            currentTemplate: {
                type: String as PropType<GeoTemplateId>,
                default: 'universal'
            },
            readOnly: {
                type: Boolean,
                default: false
            },
            error: {
                type: Boolean,
                default: false
            },
            isAnalyze: {
                type: Boolean,
                default: false
            },
            writeLoading: {
                type: Boolean,
                default: false
            },
            deferWriteCta: {
                type: Boolean,
                default: false,
            },
        },
        data: () => ({
            MAX_AI_KEYWORDS,
            keywordGroupCount: Object.keys(KeywordType).length,
            expandedLive: {} as Record<string, boolean>,
            overflowLive: {} as Record<string, boolean>,
            liveMeasureTimer: 0,
            liveOpen: false,
            citeOpen: false,
        }),
        computed: {
            article(): Article {
                return this.$store.getters.article;
            },
            keywordsForAi(): KeywordForAi[] {
                return this.$store.getters.articleKeywordsForAi || [];
            },
            lucySlotCount(): number {
                return countLucySlots(this.keywordsForAi);
            },
            keywordSelectionShown: {
                get(): boolean {
                    return this.$store.getters.keywordSelectionShown;
                },
                set(value: boolean) {
                    this.$store.commit('setKeywordSelectionShown', value);
                }
            },
            enhancing(): boolean {
                return this.$store.getters.articleAiEnhancing;
            },
            actionsDisabled(): boolean {
                return this.$store.getters.sharePreview;
            },
            manualReEnhanceCta(): boolean {
                return isManualReEnhanceArticle({
                    isAnalyze: !!this.isAnalyze,
                    enhanced: this.article.enhanced,
                    aiGenerateStatus: this.article.aiGenerateStatus,
                });
            },
            showWriteCta(): boolean {
                return shouldShowKeywordWorkspaceWriteCta({
                    sharePreview: this.actionsDisabled,
                    isAnalyze: this.isAnalyze,
                    enhanced: this.article.enhanced,
                    aiGenerateStatus: this.article.aiGenerateStatus,
                    readOnly: this.readOnly,
                    selectedKeyword: this.article.selectedKeyword || this.article.keyword,
                    hasGeneratedBody: articleHasGeneratedBody(this.article),
                    showLucyFork: this.deferWriteCta,
                    keywordSelectionShown: this.keywordSelectionShown,
                    lucySlotCount: this.lucySlotCount,
                });
            },
            writeBusy(): boolean {
                return this.enhancing || this.writeLoading;
            },
            writeCtaLabel(): string {
                if (this.isAnalyze || this.manualReEnhanceCta) {
                    return String(this.$t('article.manualEnhance.enhance'));
                }
                return String(this.$t('keywords.generateInspiration'));
            },
            enhanceWaitingForPage(): boolean {
                return enhanceWaitingForExtraction({
                    isAnalyze: !!this.isAnalyze,
                    scrapeLoading: !!this.$store.getters.articleAnalyzeScrapeLoading,
                });
            },
            writeCtaDisabled(): boolean {
                if (this.actionsDisabled || this.writeBusy || this.enhanceWaitingForPage) {
                    return true;
                }
                const slots = countLucySlots(this.keywordsForAi);
                if (slots === 0 || slots > MAX_AI_KEYWORDS) {
                    return true;
                }
                if (this.isAnalyze) {
                    return this.article.enhanced === 'processing';
                }
                return !this.article.keyword && !this.article.selectedKeyword;
            },
            writeCtaTooltip(): string {
                if (this.enhanceWaitingForPage) {
                    return String(this.$t('article.manualEnhance.loadingPage'));
                }
                if (!this.writeCtaDisabled || this.writeBusy || this.actionsDisabled) {
                    return '';
                }
                const slots = countLucySlots(this.keywordsForAi);
                if (slots === 0) {
                    return String(this.$t('keywords.lucy.writeDisabledEmpty'));
                }
                if (slots > MAX_AI_KEYWORDS) {
                    return String(this.$t('keywords.lucy.writeDisabledOver', [slots]));
                }
                return '';
            },
            headerLoading(): boolean {
                return this.loading && !this.brief.intent && !this.brief.snippetShape && !this.brief.suggestedGeoTemplate;
            },
            canApplyTemplate(): boolean {
                return !this.actionsDisabled
                    && !!this.brief.suggestedGeoTemplate
                    && this.brief.suggestedGeoTemplate !== this.currentTemplate;
            },
            hasLiveContent(): boolean {
                return this.liveCards.some((card) => !!card.text) || !!(this.brief.answers && this.brief.answers.length);
            },
            liveSectionLoading(): boolean {
                return this.liveCards.some((card) => this.liveCardLoading(card.id));
            },
            liveCards(): LiveCard[] {
                const answers = this.brief.answers || [];
                return liveCardPlatforms(answers).map((id) => {
                    const live = answers.find((item: AiAnswerSnapshot) => item.platform === id);
                    const text = liveAnswerBody(live);
                    return {
                        id,
                        text,
                        expandable: liveAnswerNeedsExpand(text),
                    };
                });
            },
            citedSources(): Array<{title: string; url: string; domain?: string; platform: string}> {
                return (this.brief.citedSources || []).filter((item) => item && item.url && item.title);
            },
            analysisJumps(): Array<{id: string; label: string}> {
                return [
                    {id: 'g-related', label: String(this.$t('keywords.list.workspace.related'))},
                    {id: 'g-questions', label: String(this.$t('keywords.list.workspace.questions'))},
                    {id: 'g-lsi', label: String(this.$t('keywords.list.workspace.lsi'))},
                    {id: 'g-long', label: String(this.$t('keywords.list.workspace.longTail'))},
                    {id: 'g-ac', label: String(this.$t('keywords.list.workspace.autocomplete'))},
                    {id: 'g-gap', label: String(this.$t('keywords.list.workspace.gap'))},
                ];
            },
            statusSentence(): string {
                if (this.error) {
                    return String(this.$t('aiBrief.connectionError'));
                }
                if (this.loading && this.hasLiveContent) {
                    return String(this.$t('aiBrief.loadingMore'));
                }
                if (this.loading) {
                    return String(this.$t('aiBrief.loading'));
                }
                if (!this.hasLiveContent) {
                    return String(this.$t('aiBrief.empty'));
                }
                return '';
            },
        },
        mounted(): void {
            EventBus.$on('collapse-keyword-analysis', this.collapseBriefLayers);
            this.scheduleLiveAnswerMeasure();
            if (typeof window !== 'undefined') {
                window.addEventListener('resize', this.scheduleLiveAnswerMeasure);
            }
        },
        updated(): void {
            this.scheduleLiveAnswerMeasure();
        },
        beforeDestroy(): void {
            EventBus.$off('collapse-keyword-analysis', this.collapseBriefLayers);
            if (this.liveMeasureTimer) {
                window.clearTimeout(this.liveMeasureTimer);
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', this.scheduleLiveAnswerMeasure);
            }
        },
        methods: {
            openLucy(): void {
                openLucyPanel({question: 'cite'});
            },
            isLiveExpanded(id: LiveBriefPlatform): boolean {
                return !!this.expandedLive[id];
            },
            showLiveToggle(card: LiveCard): boolean {
                return card.expandable || !!this.overflowLive[card.id] || this.isLiveExpanded(card.id);
            },
            toggleLiveAnswer(id: LiveBriefPlatform): void {
                this.$set(this.expandedLive, id, !this.expandedLive[id]);
                this.$nextTick(() => this.measureLiveAnswers());
            },
            scheduleLiveAnswerMeasure(): void {
                if (this.liveMeasureTimer) {
                    window.clearTimeout(this.liveMeasureTimer);
                }
                this.liveMeasureTimer = window.setTimeout(() => {
                    this.liveMeasureTimer = 0;
                    this.measureLiveAnswers();
                }, 40);
            },
            measureLiveAnswers(): void {
                const refs = this.$refs.liveAnswerBodies as HTMLElement | HTMLElement[] | undefined;
                const nodes = !refs ? [] : (Array.isArray(refs) ? refs : [refs]);
                for (const node of nodes) {
                    if (!node || !node.dataset) {
                        continue;
                    }
                    const id = node.dataset.platform as LiveBriefPlatform | undefined;
                    if (!id || this.expandedLive[id]) {
                        continue;
                    }
                    const overflows = node.scrollHeight > node.clientHeight + 1;
                    if (this.overflowLive[id] !== overflows) {
                        this.$set(this.overflowLive, id, overflows);
                    }
                }
            },
            liveCardLoading(id: LiveBriefPlatform): boolean {
                if (!this.loading) {
                    return false;
                }
                const live = (this.brief.answers || []).find((item: AiAnswerSnapshot) => item.platform === id);
                return !liveAnswerBody(live);
            },
            onKeepShape(): void {
                keepAnswerShape(this.brief.snippetShape);
                this.$emit('keep-shape');
            },
            isWorkedIn(id: LiveBriefPlatform): boolean {
                return (this.brief.workedInPlatforms || []).includes(id);
            },
            workInTip(id: LiveBriefPlatform): string {
                return String(this.$t(this.isWorkedIn(id) ? 'aiBrief.workedInTip' : 'aiBrief.workInTip'));
            },
            onWorkInLive(id: LiveBriefPlatform): void {
                if (this.actionsDisabled || this.isWorkedIn(id)) {
                    return;
                }
                this.$emit('work-in-live', id);
            },
            platformLabel(id: string): string {
                return String(this.$t('aiBrief.platforms.' + id) || id);
            },
            isCitationSelected(url: string): boolean {
                return (this.brief.selectedCitationUrls || []).some((item) => item === url);
            },
            onToggleCitation(url: string): void {
                if (this.actionsDisabled) {
                    return;
                }
                this.$emit('toggle-citation', url);
            },
            toggleKeywordAnalysis(): void {
                if (this.keywordSelectionShown) {
                    this.keywordSelectionShown = false;
                    return;
                }
                EventBus.$emit('keywords-selection-expand');
            },
            jumpToGroup(id: string): void {
                this.keywordSelectionShown = true;
                EventBus.$emit('keyword-workspace-jump', id);
            },
            enhanceArticle(): void {
                syncSelectedBriefIntoKeywords(this.article);
                EventBus.$emit('request-enhance');
            },
            onWriteCta(): void {
                if (this.writeCtaDisabled) {
                    return;
                }
                if (this.isAnalyze || this.manualReEnhanceCta) {
                    this.enhanceArticle();
                    return;
                }
                this.$emit('generate');
            },
            collapseBriefLayers(): void {
                this.liveOpen = false;
                this.citeOpen = false;
                this.expandedLive = {};
            },
            expandBriefLayers(): void {
                this.liveOpen = true;
                this.citeOpen = true;
            },
        }
    });
</script>

<style scoped lang="sass">
.writing-brief
    width: 100%
    margin: 12px auto 8px
    box-sizing: border-box

.writing-brief__strip
    background: var(--v-surface-base)
    border-radius: 12px
    padding: 12px 14px
    border: 1px solid rgba(110, 170, 52, 0.35)
    box-shadow: 0 6px 16px rgba(58, 53, 65, 0.06)
    position: relative
    overflow: hidden

.writing-brief__progress
    position: absolute
    top: 0
    left: 14px
    right: 14px
    width: auto
    max-height: 3px
    pointer-events: none
    z-index: 0

.writing-brief__sr-only
    position: absolute
    width: 1px
    height: 1px
    padding: 0
    margin: -1px
    overflow: hidden
    clip: rect(0, 0, 0, 0)
    white-space: nowrap
    border: 0

.writing-brief__head-spinner
    flex: 0 0 auto
    margin: 0 4px

.writing-brief__head
    display: flex
    align-items: flex-start
    gap: 10px
    margin-bottom: 8px
    position: relative
    z-index: 1

.writing-brief__avatar
    width: 28px
    height: 28px
    border: 0
    padding: 0
    border-radius: 50%
    background: linear-gradient(135deg, #cd7ec2, #8e3d82)
    color: #fff
    font-weight: 800
    display: inline-flex
    align-items: center
    justify-content: center
    flex: 0 0 28px
    cursor: pointer

    &:hover,
    &:focus
        outline: none
        box-shadow: 0 0 0 3px rgba(205, 126, 194, 0.45)

.writing-brief__head-copy
    flex: 1
    min-width: 0

.writing-brief__title-row
    display: flex
    align-items: center
    gap: 4px
    flex-wrap: wrap

.writing-brief__title
    font-size: 15px
    margin-right: 4px

.writing-brief__chip
    font-size: 11px !important
    height: 22px !important
    cursor: help

.writing-brief__subtitle
    font-size: 13px
    color: #6b7385
    margin-top: 2px

.writing-brief__enhance-wrap
    flex: 0 0 auto
    position: relative
    z-index: 2

.writing-brief__enhance
    white-space: nowrap

.writing-brief__template-hint
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 6px
    font-size: 13px
    background: rgba(110, 170, 52, 0.1)
    border-radius: 8px
    padding: 8px 10px
    margin-bottom: 4px

.writing-brief__layer
    margin-top: 10px

.writing-brief__layer-h
    display: flex
    align-items: center
    gap: 6px
    font-size: 11px
    font-weight: 800
    letter-spacing: .04em
    text-transform: uppercase
    color: #6b7385
    margin-bottom: 8px
    flex-wrap: wrap

.writing-brief__layer-hint
    text-transform: none
    letter-spacing: 0
    font-weight: 500

.writing-brief__num
    background: #0887dc
    color: #fff
    width: 18px
    height: 18px
    border-radius: 50%
    display: inline-flex
    align-items: center
    justify-content: center
    font-size: 10px
    font-weight: 800
    letter-spacing: 0

.writing-brief__num--live
    background: #6eaa34

.writing-brief__num--kw
    background: #8e3d82

.writing-brief__use-grid
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 10px

.writing-brief__col-title
    font-size: 11px
    font-weight: 700
    color: #6b7385
    margin-bottom: 6px
    display: flex
    align-items: center
    gap: 4px

.writing-brief__wait,
.writing-brief__empty
    font-size: 12px
    color: #6b7385
    font-style: italic
    line-height: 1.4
    margin: 0 0 6px

.writing-brief__row--skel
    min-height: 36px

.writing-brief__skel-text
    flex: 1
    min-width: 0

.writing-brief__skel-btn
    flex: 0 0 56px
    max-width: 56px

.writing-brief__chip-skel
    display: inline-flex
    align-items: center

    ::v-deep .v-skeleton-loader__chip,
    ::v-deep .v-skeleton-loader__button
        height: 22px
        margin: 0

    ::v-deep .v-skeleton-loader__button
        width: 88px

.writing-brief__hint--skel
    min-height: 88px

.writing-brief__row
    display: flex
    align-items: center
    gap: 8px
    background: var(--v-blockSurface-base)
    border-radius: 8px
    padding: 6px 8px
    margin-bottom: 6px

    p
        font-size: 13px
        flex: 1
        min-width: 0
        margin: 0
        line-height: 1.35

.writing-brief__row-actions
    display: flex
    flex-wrap: wrap
    gap: 4px
    justify-content: flex-end
    flex: 0 0 auto

.writing-brief__btn-h2
    background: #e8f4fc !important
    color: var(--sw-primary-fill) !important

.writing-brief__missing
    color: #6b7385
    font-size: 12px

.writing-brief__live-grid
    display: grid
    grid-template-columns: 1fr 1fr 1fr 1fr
    gap: 8px
    padding: 0 10px 10px

.writing-brief__jumps
    display: flex
    flex-wrap: wrap
    gap: 6px
    margin-top: 8px

.writing-brief__jump
    border: 1px solid #d5dce8
    background: #fff
    color: #334
    border-radius: 999px
    font-size: 11px
    font-weight: 700
    padding: 4px 9px
    cursor: pointer

    &:hover
        border-color: #0887dc
        color: var(--sw-primary-fill)

.kw-row--cite
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 8px
    align-items: start
    padding: 8px 10px
    border-bottom: 1px solid #edf0f5
    text-align: left

.kw-row__mid
    min-width: 0

.kw-row__pills
    display: flex
    gap: 4px
    flex-wrap: wrap
    justify-content: flex-end
    padding-top: 1px

.kw-pill
    height: 22px !important
    min-width: 74px
    padding: 0 8px !important
    border-radius: 4px !important
    font-size: 11px !important
    font-weight: 700 !important
    line-height: 22px !important

    .v-icon
        color: inherit !important

.kw-pill--lucy
    background: #fff !important
    color: #8e3d82 !important
    box-shadow: inset 0 0 0 1px #cd7ec2 !important

.kw-pill--on
    background: #6eaa34 !important
    color: var(--sw-on-accent) !important
    box-shadow: none !important

.kw-row__phrase
    font-size: 13px
    font-weight: 700
    line-height: 1.35

.kw-row__meta
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 4px
    margin-top: 4px

.kw-src
    display: inline-flex
    align-items: center
    height: 18px
    padding: 0 6px
    border-radius: 999px
    font-size: 10px
    font-weight: 800
    border: 1px solid #e4b7dd
    color: #8e3d82
    background: #fbf4fa

.writing-brief__cite-hint
    font-size: 12px
    color: #6b7385

.writing-brief__hint
    background: rgba(110, 170, 52, 0.08)
    border-left: 3px solid #6eaa34
    border-radius: 8px
    padding: 8px 10px
    font-size: 12.5px
    line-height: 1.4

    p
        margin: 0

    .writing-brief__empty
        margin-bottom: 6px

.writing-brief__hint-title
    font-weight: 800
    font-size: 12px
    margin-bottom: 4px

.writing-brief__answer-wrap
    margin-bottom: 4px

.writing-brief__answer
    margin: 0 0 4px
    white-space: pre-wrap
    overflow-wrap: anywhere
    word-break: break-word
    line-height: 1.4

.writing-brief__answer--collapsed
    max-height: 7em
    overflow: hidden
    -webkit-mask-image: linear-gradient(to bottom, #000 4.2em, transparent)
    mask-image: linear-gradient(to bottom, #000 4.2em, transparent)

.writing-brief__answer-toggle
    display: inline-flex
    min-width: 0
    height: 22px !important
    margin: 0 0 2px -6px
    padding: 0 6px !important
    color: #3d6b1f !important
    font-weight: 700

.writing-brief__facts
    margin-top: 10px

.writing-brief__citations
    margin-top: 10px

.writing-brief__cite
    display: flex
    align-items: center
    gap: 8px
    background: var(--v-blockSurface-base)
    border-radius: 8px
    padding: 6px 8px
    margin-bottom: 6px

.writing-brief__cite-copy
    flex: 1
    min-width: 0
    display: flex
    flex-direction: column
    font-size: 12.5px
    line-height: 1.35

    strong
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

    span
        color: #6b7385
        font-size: 12px

.writing-brief__fact
    margin-bottom: 8px

.writing-brief__kw-bar
    display: flex
    align-items: center
    justify-content: space-between
    gap: 8px
    flex-wrap: wrap
    background: var(--v-blockSurface-base)
    border: 1px dashed #cfd8e6
    border-radius: 10px
    padding: 8px 12px

.writing-brief__kw-toggle
    min-height: 36px !important

.writing-brief__kw-meta
    font-size: 13px
    color: #6b7385
    margin-left: 8px

.writing-brief__status
    font-size: 13px
    color: #6b7385
    font-style: italic
    margin: 6px 2px 0

@media (max-width: 1260px)
    .writing-brief__live-grid
        grid-template-columns: 1fr 1fr

@media (max-width: 959px)
    .kw-row--cite
        grid-template-columns: minmax(0, 1fr)

    .kw-row--cite .kw-row__pills
        justify-content: flex-start

    .writing-brief__head
        flex-wrap: wrap

    .writing-brief__enhance-wrap
        width: 100%

        .writing-brief__enhance
            width: 100%

    .writing-brief__use-grid,
    .writing-brief__live-grid
        grid-template-columns: 1fr

    .writing-brief__row
        flex-wrap: wrap

        p
            flex: 1 1 100%

.theme--dark
    .writing-brief__strip
        border-color: rgba(110, 170, 52, 0.45)

    .writing-brief__subtitle,
    .writing-brief__col-title,
    .writing-brief__layer-h,
    .writing-brief__kw-meta,
    .writing-brief__status,
    .writing-brief__missing,
    .writing-brief__wait,
    .writing-brief__empty
        color: #a8b0c2

    .writing-brief__cite-copy span
        color: #a8b0c2

    .writing-brief__answer-toggle
        color: #b5d48a !important

    .writing-brief__kw-bar
        border-color: #4a5368

    .writing-brief__btn-h2
        background: rgba(8, 135, 220, 0.22) !important
        color: #81C2ED !important
</style>
